import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { fetchReviewQueue, updateReviewItem } from '@/lib/api'
import type { ReviewItem } from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { Loader2, Check, X, ExternalLink, RefreshCw } from 'lucide-react'

export function ReviewQueue() {
  const [items, setItems] = useState<ReviewItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updating, setUpdating] = useState<string | null>(null)

  const loadItems = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchReviewQueue({ status: 'pending' })
      setItems(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadItems()
  }, [loadItems])

  const handleAction = useCallback(
    async (reviewId: string, status: 'approved' | 'rejected' | 'merged') => {
      setUpdating(reviewId)
      try {
        await updateReviewItem(reviewId, { status })
        setItems((prev) => prev.filter((i) => i.review_id !== reviewId))
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to update')
      } finally {
        setUpdating(null)
      }
    },
    []
  )

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header - asymmetric padding */}
      <header className="border-b border-border pl-6 pr-8 md:pl-8 md:pr-12 lg:pl-12 lg:pr-20 py-6 bg-card">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-light">Review Queue</h1>
            <p className="text-sm text-muted-foreground">
              {items.length} items pending review
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={loadItems}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      {/* Content - generous spacing */}
      <div className="flex-1 overflow-auto pl-6 pr-8 md:pl-8 md:pr-12 lg:pl-12 lg:pr-20 py-8 md:py-12">
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
            {error}
          </div>
        )}

        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Check className="h-12 w-12 text-foreground mb-4" />
            <p className="text-lg font-light">All caught up!</p>
            <p className="text-sm text-muted-foreground">
              No items pending review
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <ReviewItemCard
                key={item.review_id}
                item={item}
                updating={updating === item.review_id}
                onAction={handleAction}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function ReviewItemCard({
  item,
  updating,
  onAction,
}: {
  item: ReviewItem
  updating: boolean
  onAction: (id: string, status: 'approved' | 'rejected' | 'merged') => void
}) {
  // Monochromatic badge styles per Zinsser guide
  const kindColors: Record<string, string> = {
    low_confidence: 'bg-muted text-muted-foreground',
    borderline_duplicate: 'bg-muted text-muted-foreground',
    potential_merge: 'bg-muted text-muted-foreground',
    default: 'bg-muted text-muted-foreground',
  }

  return (
    <div
      className={cn(
        'p-4 rounded-lg border border-border bg-card',
        updating && 'opacity-50'
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Kind badge */}
          <span
            className={cn(
              'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium mb-2',
              kindColors[item.kind] || kindColors.default
            )}
          >
            {item.kind.replace('_', ' ')}
          </span>

          {/* Reason */}
          <p className="text-sm mb-2">{item.reason}</p>

          {/* Similarity score if present */}
          {item.similarity_score !== null && (
            <p className="text-xs text-muted-foreground">
              Similarity: {Math.round(item.similarity_score * 100)}%
            </p>
          )}

          {/* Links to cards */}
          <div className="flex items-center gap-4 mt-3">
            {item.card_id && (
              <Link
                to={`/cards/${item.card_id}`}
                className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              >
                View Card <ExternalLink className="h-3 w-3" />
              </Link>
            )}
            {item.other_card_id && (
              <Link
                to={`/cards/${item.other_card_id}`}
                className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              >
                Compare Card <ExternalLink className="h-3 w-3" />
              </Link>
            )}
          </div>
        </div>

        {/* Actions - monochromatic */}
        <div className="flex flex-col gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onAction(item.review_id, 'approved')}
            disabled={updating}
            className="text-foreground hover:bg-muted/50"
          >
            <Check className="h-4 w-4 mr-1" />
            Approve
          </Button>
          {item.kind === 'potential_merge' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onAction(item.review_id, 'merged')}
              disabled={updating}
              className="text-foreground hover:bg-muted/50"
            >
              Merge
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => onAction(item.review_id, 'rejected')}
            disabled={updating}
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <X className="h-4 w-4 mr-1" />
            Reject
          </Button>
        </div>
      </div>
    </div>
  )
}
